<template>
	<div class="user-settings">
		<!-- Header -->
		<div class="header">
			<div class="header-left">
				<h2 class="title active">设置</h2>
			</div>
			<div class="header-right">
				<span class="nav-link" :class="{ 'active-link': currentTab === 'feedback' }"
					@click="currentTab = 'feedback'">意见反馈</span>
				<span class="nav-link" :class="{ 'active-link': currentTab === 'about' }"
					@click="handleTabChange('about')">关于平台</span>
				<span class="nav-link" :class="{ 'active-link': currentTab === 'agreement' }"
					@click="handleTabChange('agreement')">用户协议</span>
				<span class="nav-link" :class="{ 'active-link': currentTab === 'privacy' }"
					@click="handleTabChange('privacy')">隐私政策</span>
				<span class="nav-link" :class="{ 'active-link': currentTab === 'password' }"
					@click="currentTab = 'password'">修改密码</span>
			</div>
		</div>

		<!-- Feedback Form Content -->
		<div class="settings-content" v-if="currentTab === 'feedback'">
			<!-- Feedback Issue -->
			<div class="form-group">
				<label>反馈问题和建议</label>
				<div class="input-wrapper">
					<textarea placeholder="请详细描述所遇到的问题" v-model="formData.content"></textarea>
				</div>
			</div>

			<!-- Upload Image -->
			<div class="form-group">
				<label>上传图片</label>
				<div class="upload-box">
					<div class="upload-preview" v-if="formData.imgs">
						<img :src="formData.imgs" alt="Uploaded Image" class="preview-img" />
						<div class="remove-btn" @click="formData.imgs = ''">×</div>
					</div>
					<div class="upload-trigger" @click="triggerUpload" v-else>
						<span class="plus-icon">+</span>
						<span class="upload-text">点击上传</span>
					</div>
					<input type="file" ref="fileInput" @change="handleUpload" style="display: none" accept="image/*">
				</div>
			</div>

			<!-- Contact Info -->
			<div class="form-group">
				<label>联系方式</label>
				<div class="input-wrapper">
					<input type="text" placeholder="请输入联系方式" v-model="formData.contact" />
				</div>
			</div>

			<!-- Submit Button -->
			<div class="form-actions">
				<button class="submit-btn" @click="handleSubmit">确定</button>
			</div>
		</div>

		<!-- Password Form Content -->
		<div class="password-content" v-if="currentTab === 'password'">
			<h3 class="form-title">修改密码</h3>

			<div class="form-group">
				<div class="input-wrapper">
					<input type="text" placeholder="请输入手机号" v-model="passwordForm.phone" />
				</div>
			</div>

			<div class="form-group">
				<div class="input-wrapper with-suffix">
					<input type="text" placeholder="请输入验证码" v-model="passwordForm.code" />
					<span class="suffix-btn" @click="sendVerifyCode" :style="{ cursor: isSending ? 'not-allowed' : 'pointer', color: isSending ? '#999' : '#ff9900' }">{{ codeText }}</span>
				</div>
			</div>

			<div class="form-group">
				<div class="input-wrapper">
					<input type="password" placeholder="请设置6-12位密码" v-model="passwordForm.password" />
				</div>
			</div>

			<div class="form-actions">
				<button class="submit-btn" @click="handlePasswordSubmit">确定</button>
			</div>
		</div>

		<!-- Rich Text Content (About, Agreement, Privacy) -->
		<div class="settings-content" v-if="['about', 'agreement', 'privacy'].includes(currentTab)">
			<div class="rich-text" v-html="currentContent" v-if="!loading && currentContent"></div>
			<div class="loading-state" v-if="loading">加载中...</div>
			<div class="empty-state" v-if="!loading && !currentContent">暂无内容</div>
		</div>
	</div>
</template>

<script>
	import {
		submitFeedback,
		sendCode,
		resetPwd
	} from '@/api/user'
	import { ElMessage } from 'element-plus'
	import 'element-plus/es/components/message/style/css'
	import {
		uploadFile
	} from '@/api/common'
	import {
		getSetting
	} from '@/api/public'
	// wait, getSetting is in public.js, not common.js? 
	// I need to check where getSetting is imported from.
	// In PageFooter it was imported from '@/api/public'.
	// Let me fix the import in the next step or here if I can confirm.

	// I will use two separate replaces to be safe. 
	// First let's fix the import part.


	export default {
		name: 'UserSettings',
		data() {
			return {
				currentTab: 'feedback',
				formData: {
					content: '',
					contact: '',
					imgs: ''
				},
				passwordForm: {
					phone: '',
					code: '',
					password: ''
				},
				aboutContent: '',
				agreementContent: '',
				privacyContent: '',
				loading: false,
				codeText: '验证码',
				isSending: false,
				timer: null
			}
		},
		computed: {
			currentContent() {
				if (this.currentTab === 'about') return this.aboutContent
				if (this.currentTab === 'agreement') return this.agreementContent
				if (this.currentTab === 'privacy') return this.privacyContent
				return ''
			}
		},
		methods: {
			async handlePasswordSubmit() {
				const { phone, code, password } = this.passwordForm
				if (!phone) {
					ElMessage.warning('请输入手机号')
					return
				}
				if (!code) {
					ElMessage.warning('请输入验证码')
					return
				}
				if (!password) {
					ElMessage.warning('请输入新密码')
					return
				}
				if (password.length < 6 || password.length > 12) {
					ElMessage.warning('密码长度请在6-12位之间')
					return
				}

				try {
					await resetPwd({
						phone,
						password,
						code
					})
					ElMessage.success('密码修改成功，请重新登录')
					this.passwordForm = { phone: '', code: '', password: '' }
				} catch (error) {
					console.error(error)
					ElMessage.error(error.message || '请求失败')
				}
			},
			async sendVerifyCode() {
				if (this.isSending) return

				const phone = this.passwordForm.phone
				if (!phone) {
					ElMessage.warning('请输入手机号')
					return
				}
				if (!/^1[3-9]\d{9}$/.test(phone)) {
					ElMessage.warning('请输入正确的手机号')
					return
				}

				this.isSending = true
				try {
					await sendCode({
						phone
					})
					ElMessage.success('验证码发送成功')
					let count = 60
					this.codeText = `${count}s`
					this.timer = setInterval(() => {
						count--
						if (count > 0) {
							this.codeText = `${count}s`
						} else {
							this.stopTimer()
						}
					}, 1000)
				} catch (error) {
					this.isSending = false
					console.error(error)
					ElMessage.error(error.message || '发送验证码出错')
				}
			},
			stopTimer() {
				if (this.timer) {
					clearInterval(this.timer)
					this.timer = null
				}
				this.isSending = false
				this.codeText = '验证码'
			},
			handleTabChange(tab) {
				this.currentTab = tab
				if (['about', 'agreement', 'privacy'].includes(tab)) {
					this.fetchSetting(tab)
				}
			},
			async fetchSetting(tab) {
				let key = ''
				if (tab === 'about') key = 'init'
				if (tab === 'agreement') key = 'user'
				if (tab === 'privacy') key = 'secret'

				// 如果已经有内容，不再请求
				if (this.currentContent) return

				this.loading = true
				try {
					const res = await getSetting({
						groupKey: key
					})
					// 根据返回结构处理，假设 res.data 或 res 是内容
					// let content = res.data
					// Ensure content is a string


					if (tab === 'about') this.aboutContent = res.about
					if (tab === 'agreement') this.agreementContent = res.user
					if (tab === 'privacy') this.privacyContent = res.secret
				} catch (error) {
					console.error(error)
				} finally {
					this.loading = false
				}
			},
			triggerUpload() {
				this.$refs.fileInput.click()
			},
			async handleUpload(event) {
				const file = event.target.files[0]
				if (!file) return

				const formData = new FormData()
				formData.append('file', file)

				try {
					const res = await uploadFile(formData)
					if (res && res.code === 200) {
						// Assuming the API returns the URL in data or data.url
						this.formData.imgs = res.data?.url || res.data || res.url
					} else {
						alert((res && res.msg) || '上传失败')
					}
				} catch (error) {
					console.error(error)
					alert('上传出错')
				}
			},
			async handleSubmit() {
				if (!this.formData.content) {
					alert('请输入反馈内容')
					return
				}
				if (!this.formData.contact) {
					alert('请输入联系方式')
					return
				}

				try {
					const res = await submitFeedback(this.formData)
					console.log(res)
					if (res && res.code == 200) {
						alert('反馈提交成功')
						this.formData = {
							content: '',
							contact: '',
							imgs: ''
						}
					} else {
						alert((res && res.msg) || '提交失败')
					}
				} catch (error) {
					console.error(error)
					alert('提交出错')
				}
			}
		}
	}
</script>

<style scoped lang="scss">
	.user-settings {
		background-color: #1e1e1e;
		border-radius: 8px;
		min-height: 600px;
		padding: 0;
		color: #fff;

		.header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 20px 40px;
			border-bottom: 1px solid #333;

			.header-left {
				.title {
					font-size: 18px;
					font-weight: normal;
					margin: 0;
					padding-bottom: 15px;
					border-bottom: 2px solid #ff9900;
					color: #fff;
					position: relative;
					top: 21px; // Align with border
				}
			}

			.header-right {
				display: flex;
				gap: 30px;

				.nav-link {
					font-size: 14px;
					color: #999;
					cursor: pointer;
					transition: color 0.3s;

					&:hover {
						color: #fff;
					}

					&.active-link {
						color: #ff9900;
					}
				}
			}
		}

		.settings-content {
			padding: 40px;
			max-width: 800px;

			.form-group {
				margin-bottom: 30px;

				label {
					display: block;
					font-size: 16px;
					color: #ccc;
					margin-bottom: 15px;
				}

				.input-wrapper {
					textarea {
						width: 100%;
						height: 120px;
						background-color: #2a2a2a;
						border: 1px solid #444;
						border-radius: 4px;
						padding: 15px;
						color: #fff;
						font-size: 14px;
						resize: none;
						box-sizing: border-box;

						&::placeholder {
							color: #666;
						}

						&:focus {
							outline: none;
							border-color: #ff9900;
						}
					}

					input {
						width: 100%;
						height: 45px;
						background-color: #2a2a2a;
						border: 1px solid #444;
						border-radius: 4px;
						padding: 0 15px;
						color: #fff;
						font-size: 14px;
						box-sizing: border-box;

						&::placeholder {
							color: #666;
						}

						&:focus {
							outline: none;
							border-color: #ff9900;
						}
					}
				}

				.upload-box {
					.upload-preview {
						width: 100px;
						height: 100px;
						position: relative;
						border: 1px solid #444;
						border-radius: 4px;
						overflow: hidden;

						.preview-img {
							width: 100%;
							height: 100%;
							object-fit: cover;
						}

						.remove-btn {
							position: absolute;
							top: 0;
							right: 0;
							width: 20px;
							height: 20px;
							background-color: rgba(0, 0, 0, 0.5);
							color: #fff;
							display: flex;
							align-items: center;
							justify-content: center;
							cursor: pointer;
							font-size: 14px;

							&:hover {
								background-color: rgba(0, 0, 0, 0.8);
							}
						}
					}

					.upload-trigger {
						width: 100px;
						height: 100px;
						background-color: #2a2a2a;
						border: 1px dashed #666;
						border-radius: 4px;
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: center;
						cursor: pointer;
						transition: all 0.3s;

						&:hover {
							border-color: #ff9900;
							color: #ff9900;
						}

						.plus-icon {
							font-size: 30px;
							color: #999;
							margin-bottom: 5px;
						}

						.upload-text {
							font-size: 12px;
							color: #666;
						}
					}
				}
			}

			.form-actions {
				margin-top: 50px;
				text-align: center;

				.submit-btn {
					width: 300px;
					height: 45px;
					background-color: #ff9900;
					border: none;
					border-radius: 22.5px;
					color: #fff;
					font-size: 16px;
					cursor: pointer;
					transition: background-color 0.3s;

					&:hover {
						background-color: #e68a00;
					}
				}
			}
		}

		.password-content {
			padding: 40px;
			max-width: 400px;
			margin: 0 auto;
			padding-top: 80px;

			.form-title {
				text-align: center;
				font-size: 18px;
				color: #fff;
				margin-bottom: 40px;
				font-weight: normal;
			}

			.form-group {
				margin-bottom: 25px;

				.input-wrapper {
					position: relative;

					input {
						width: 100%;
						height: 50px;
						background-color: #2a2a2a;
						border: 1px solid #444;
						border-radius: 4px;
						padding: 0 15px;
						color: #fff;
						font-size: 14px;
						box-sizing: border-box;

						&::placeholder {
							color: #666;
						}

						&:focus {
							outline: none;
							border-color: #ff9900;
						}
					}

					&.with-suffix {
						input {
							padding-right: 80px;
						}

						.suffix-btn {
							position: absolute;
							right: 15px;
							top: 50%;
							transform: translateY(-50%);
							color: #ff9900;
							font-size: 14px;
							cursor: pointer;

							&:hover {
								text-decoration: underline;
							}
						}
					}
				}
			}

			.form-actions {
				margin-top: 50px;
				text-align: center;

				.submit-btn {
					width: 100%;
					height: 45px;
					background-color: #ff9900;
					border: none;
					border-radius: 22.5px;
					color: #fff;
					font-size: 16px;
					cursor: pointer;
					transition: background-color 0.3s;

					&:hover {
						background-color: #e68a00;
					}
				}
			}
		}
	}

	.rich-text {
		color: #ddd;
		line-height: 1.6;

		::v-deep img {
			max-width: 100%;
			height: auto;
			display: block;
			margin: 10px 0;
		}

		::v-deep p {
			margin-bottom: 15px;
		}
	}

	.loading-state,
	.empty-state {
		text-align: center;
		padding: 50px 0;
		color: #666;
		font-size: 14px;
	}
</style>